import { RefObject, useEffect, useRef } from "react"

type SelectedOfferScrollOptions = {
  galleryRef: RefObject<HTMLElement>
  scrollContainerRef: RefObject<HTMLElement>
  resourceId: number
  offerId: number | null
  layoutIsReady: boolean
}

/** Reveal a selected Offer without moving focus or repeating on access refetches. */
export function useSelectedOfferScroll({
  galleryRef,
  scrollContainerRef,
  resourceId,
  offerId,
  layoutIsReady
}: SelectedOfferScrollOptions) {
  const selection = useRef<{ key: string | null; scrolled: boolean }>({
    key: null,
    scrolled: false
  })
  const key = offerId == null ? null : `${resourceId}:${offerId}`

  useEffect(() => {
    if (selection.current.key !== key)
      selection.current = { key, scrolled: false }
    if (!key || !layoutIsReady || selection.current.scrolled) return

    // Wait for the resource and card layout to commit. Scope the lookup to the
    // main gallery, excluding duplicate cards in expanded dialogs.
    const frame = requestAnimationFrame(() => {
      const card = galleryRef.current?.querySelector<HTMLElement>(
        `.CoverPageOfferCard[data-offer-id="${offerId}"]`
      )
      const viewport = scrollContainerRef.current
      if (!card || !viewport) return
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? "instant"
        : "smooth"
      const rect = card.getBoundingClientRect()
      const view = viewport.getBoundingClientRect()

      // Scroll only the Cover Page and carousel, preserving the panel's scroll
      // position and keyboard focus. Center distant cards in the available space.
      if (rect.top < view.top + 16 || rect.bottom > view.bottom - 16) {
        const inset = Math.max(16, (view.height - rect.height) / 2)
        viewport.scrollTo({
          top: Math.max(0, viewport.scrollTop + rect.top - view.top - inset),
          behavior
        })
      }
      const carousel = card.closest<HTMLElement>(".carousel-scroller")
      if (carousel) {
        const bounds = carousel.getBoundingClientRect()
        carousel.scrollTo({
          left:
            carousel.scrollLeft +
            rect.left -
            bounds.left -
            (bounds.width - rect.width) / 2,
          behavior
        })
      }
      selection.current.scrolled = true
    })
    return () => cancelAnimationFrame(frame)
  }, [galleryRef, scrollContainerRef, key, offerId, layoutIsReady])
}
